import { useEffect, useMemo, useRef, useState } from 'react';
import {
    getPanelResizeLimit,
    getPanelShape,
    PANEL_RESIZE_LIMIT,
    PANEL_SCROLL_THRESHOLD,
    PANEL_VIEW,
    RESIZING_BODY_CLASS
} from '../Model/Model';
import { getAreaGroups, getPanelHeader, getPanelView, isConfiguredPanel } from './EventMonitoringCommon';

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Logic of a panel: header, events of its source, the next page read when its body is scrolled
 * to the bottom and the resize interaction of its corner handle.
 */
const PanelApp = ({ panel, columns, rows, feed, t, onLoadMore, onResize, onResizeEnd }) => {
    const panelRef = useRef(null);
    const bodyRef = useRef(null);
    const resizeRef = useRef(null);
    const listenersRef = useRef(null);
    const [isResizing, setIsResizing] = useState(false);
    const [isHandleHover, setIsHandleHover] = useState(false);
    // Limit of the size the pointer of the resize is past, null while the panel follows it //
    const [resizeLimit, setResizeLimit] = useState(null);

    const configured = isConfiguredPanel(panel);
    const view = getPanelView(panel);
    const header = getPanelHeader(panel, t);

    const panelEvents = feed.events;
    const isGrouped = view === PANEL_VIEW.GROUPED;
    // A grouped panel shows the areas of its zone, every area reads its own events once it is expanded //
    const showEmpty = isGrouped ? feed.areas?.length === 0 : feed.loaded && panelEvents.length === 0;
    const groups = useMemo(
        () => (isGrouped ? getAreaGroups(feed.areas ?? [], feed.areaFeeds) : []),
        [isGrouped, feed.areas, feed.areaFeeds]
    );

    /**
     * Reads the next page once the body is near its bottom. A body whose events do not fill it
     * has no scroll, so it reads the next page right away. The grouped panels do not read pages of
     * the zone here, the expanded areas read their own.
     */
    const loadMoreIfNeeded = () => {
        const body = bodyRef.current;
        if (isGrouped || !body || !feed.loaded || !feed.hasMore || feed.loading) return;

        const { scrollTop, scrollHeight, clientHeight } = body;
        if (scrollHeight - scrollTop <= clientHeight + PANEL_SCROLL_THRESHOLD) onLoadMore(panel);
    };

    // Each page and each resize can leave the body without scroll //
    useEffect(loadMoreIfNeeded, [feed, panel.colSpan, panel.rowSpan]);

    const panelClassName = [
        'event-monitoring-panel background-general-00 border-radius-12px',
        `event-monitoring-col-span-${Math.min(panel.colSpan, columns)}`,
        `event-monitoring-row-span-${Math.min(panel.rowSpan, rows)}`,
        resizeLimit
            ? 'border-full-warning-400'
            : isResizing || isHandleHover
                ? 'border-full-secondary-150'
                : !configured ? 'border-dashed-shades-300' : 'border-full-transparent',
    ].join(' ');

    // The handle turns to warning while the panel can not follow the pointer //
    const handleClassName = [
        'event-monitoring-resize-handle w-24px h-24px border-radius-4px',
        resizeLimit ? 'border-full-warning-400 background-warning-100' : 'border-full-secondary-150 background-secondary-100',
    ].join(' ');
    const handleIconClassName = `btn-icon-svg-16px ${resizeLimit ? 'icon-color-warning-700' : 'icon-color-secondary-600'}`;

    /**
     * Shows the limit the pointer is past: the cursor of the page and the colors of the panel and its handle.
     */
    const showResizeLimit = (limit) => {
        Object.values(PANEL_RESIZE_LIMIT).forEach(({ BODY_CLASS }) => document.body.classList.remove(BODY_CLASS));
        if (limit) document.body.classList.add(limit.BODY_CLASS);
        setResizeLimit(limit);
    };

    /**
     * Converts the pointer offset into spans of the grid. The size of one cell is taken from the
     * current size of the panel, so the result follows the canvas whatever its width is.
     */
    const handlePointerMove = (e) => {
        const start = resizeRef.current;
        if (!start) return;

        const colSpan = (start.width + start.gap + (e.clientX - start.x)) / start.cellWidth;
        const rowSpan = (start.height + start.gap + (e.clientY - start.y)) / start.cellHeight;
        const { colSpan: nextColSpan, rowSpan: nextRowSpan } = getPanelShape(colSpan, rowSpan, columns, rows);

        const limit = getPanelResizeLimit(colSpan, rowSpan, columns, rows);
        if (limit !== start.limit) {
            resizeRef.current = { ...resizeRef.current, limit };
            showResizeLimit(limit);
        }

        if (nextColSpan !== start.colSpan || nextRowSpan !== start.rowSpan) {
            resizeRef.current = { ...resizeRef.current, colSpan: nextColSpan, rowSpan: nextRowSpan };
            onResize(panel.id, nextColSpan, nextRowSpan);
        }
    };

    /**
     * Releases the window listeners registered by the resize that is in progress.
     */
    const removeListeners = () => {
        const listeners = listenersRef.current;
        if (!listeners) return;
        window.removeEventListener('pointermove', listeners.move);
        window.removeEventListener('pointerup', listeners.up);
        window.removeEventListener('pointercancel', listeners.up);
        listenersRef.current = null;
        document.body.classList.remove(RESIZING_BODY_CLASS);
        Object.values(PANEL_RESIZE_LIMIT).forEach(({ BODY_CLASS }) => document.body.classList.remove(BODY_CLASS));
    };

    /**
     * Ends the resize. The new size is stored once, when the pointer is released and only if it changed.
     */
    const handlePointerUp = () => {
        const start = resizeRef.current;
        if (start && (start.colSpan !== start.initialColSpan || start.rowSpan !== start.initialRowSpan)) onResizeEnd();
        resizeRef.current = null;
        setIsResizing(false);
        setResizeLimit(null);
        removeListeners();
    };

    const handleResizeStart = (e) => {
        if (!panelRef.current) return;
        e.preventDefault();

        const { width, height } = panelRef.current.getBoundingClientRect();
        // A cell is one track plus the gap of the grid, so a span of n measures n cells minus one gap //
        const gap = parseFloat(window.getComputedStyle(panelRef.current.parentElement).rowGap) || 0;
        resizeRef.current = {
            x: e.clientX,
            y: e.clientY,
            width,
            height,
            gap,
            cellWidth: (width + gap) / Math.min(panel.colSpan, columns),
            cellHeight: (height + gap) / Math.min(panel.rowSpan, rows),
            colSpan: panel.colSpan,
            rowSpan: panel.rowSpan,
            initialColSpan: panel.colSpan,
            initialRowSpan: panel.rowSpan,
            limit: null,
        };
        setIsResizing(true);
        removeListeners();
        // The resize cursor is kept on the whole page, so it does not drop while the pointer is off the handle //
        document.body.classList.add(RESIZING_BODY_CLASS);
        listenersRef.current = { move: handlePointerMove, up: handlePointerUp };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    };

    // The listeners are released if the panel is removed while it is being resized //
    useEffect(() => removeListeners, []);

    return {
        panelRef,
        panelClassName,
        handleClassName,
        handleIconClassName,
        configured,
        view,
        header,
        bodyRef,
        panelEvents,
        showEmpty,
        // The pages of events are read without the overlay of the screen, so the panel shows its own loader from its first page //
        loadingEvents: isGrouped ? !feed.areas && feed.loading : feed.loading,
        groups,
        loadMoreIfNeeded,
        handleResizeStart,
        setIsHandleHover
    };
};

export default PanelApp;
